import React, { useCallback, useRef, useContext } from 'react';
import Loading from 'components/Loading';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddButton from 'ui/Buttons/AddButton';
import { trackEvent } from 'helpers/trackerHelpers';
import ImportWorkflow from 'routes/ImportCsv/ImportWorkflow/ImportWorkflow';
import { useNavigate, useParams } from 'react-router-dom';
import { FILES_IMPORT } from '../helpers';
import DomainDetails, { TableT } from './DomainDetails';
import TableDetails from './TableDetails';
import NewTableContext from '../context/NewTable/NewTableContext';
import { useShouldShowFilesImportV2 } from '../hooks/csvAccess';
import Forbidden from 'components/Errors/Forbidden';

interface CellarContentProps {
    isLoading: boolean;
    tables: TableT[];
}

const CellarContent = ({ isLoading, tables }: CellarContentProps) => {
    const { t } = useTranslation();
    const shouldShowFilesImportV2 = useShouldShowFilesImportV2();

    const importWorkflowRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const { func: selectedFunction = '', table: selectedTable = '' } =
        useParams();
    const { isNewTable, setIsNewTable } = useContext(NewTableContext);
    const createNewTable = useCallback(() => {
        trackEvent('settings/data/csv-imports-management/nouvelle-table-click');
        setIsNewTable(true);
        navigate(`/cellar/filesImport`);
    }, []);

    const isCsvImportEnabled =
        selectedFunction === FILES_IMPORT && shouldShowFilesImportV2;

    if (isLoading) {
        return (
            <Box className="content">
                <Loading />
            </Box>
        );
    }

    if ((selectedTable || isNewTable) && isCsvImportEnabled) {
        return (
            <Box ref={importWorkflowRef} className="content">
                <ImportWorkflow
                    tableMetadata={tables.find(
                        (table) => table.value === selectedTable,
                    )}
                    parentRef={importWorkflowRef}
                    initialStep={
                        isNewTable ? 'selectFileStep' : 'showTableStep'
                    }
                />
            </Box>
        );
    }

    if (selectedTable) {
        return (
            <Box className="content">
                <TableDetails />
            </Box>
        );
    }
    if (selectedFunction === FILES_IMPORT && !shouldShowFilesImportV2) {
        return <Forbidden sx={{ width: '100%' }} />;
    }
    return (
        <Box className="content">
            <DomainDetails domain={selectedFunction} tables={tables}>
                {isCsvImportEnabled ? (
                    <Box>
                        <AddButton
                            text={t('importCSV.newTable')}
                            onClick={createNewTable}
                        />
                    </Box>
                ) : null}
            </DomainDetails>
        </Box>
    );
};

export default React.memo(CellarContent);
